<template>
  <div>
    <v-text-field
      :value="selectedLabel"
      :placeholder="placeholder"
      :rules="rules"
      :disabled="disabled"
      :loading="loading && autoFetch"
      :hide-details="hideDetails"
      readonly
      clearable
      append-icon="mdi-chevron-down"
      @click="openDialog"
      @click:append="openDialog"
      @click:clear="clearSelection"
    />

    <v-dialog v-model="dialog" max-width="900px">
      <v-card>
        <v-card-title class="pa-3 pb-0">
          <span>Select account</span>
        </v-card-title>

        <v-card-text class="pa-3">
          <v-text-field
            v-model="searchDraft"
            placeholder="Search by name"
            prepend-inner-icon="mdi-magnify"
            clearable
            dense
            hide-details
            @click:clear="onClearSearch"
            @blur="onSearchBlur"
            @keydown.enter="onSearchBlur"
          />

          <v-data-table
            :loading="loading"
            :headers="columns"
            :items="displayAccounts"
            :items-per-page="-1"
            item-key="accountId"
            hide-default-footer
            no-data-text="No available accounts"
            class="elevation-0 mt-2"
          >
            <template v-slot:[`item.action`]="{ item }">
              <v-btn v-if="item.accountId === selectedAccountId" color="primary" icon small>
                <v-icon>mdi-check</v-icon>
              </v-btn>
              <v-btn v-else color="primary" outlined small @click="selectAccount(item)">Select</v-btn>
            </template>
          </v-data-table>

          <div class="d-flex justify-end mt-2" v-if="pagination.totalPages > 1">
            <v-pagination
              :length="pagination.totalPages"
              v-model="pagination.page"
              prev-icon="mdi-menu-left"
              next-icon="mdi-menu-right"
              :total-visible="7"
            />
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <div v-if="selectedAccountId" class="d-flex align-center line-clamp-1">
            <div>
              Selected account:
            </div>
            <div style="flex: 1;" class="font-weight-bold text-truncate ml-1">
              {{ value ? (accountById[value] && accountById[value].accountName) || value : '' }}
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { addFeeApi } from '@/apis/addFee';

export default {
  name: 'AvailableAccountSelector',
  props: {
    value: {
      type: String,
      default: null,
    },
    autoFetch: {
      type: Boolean,
      default: true,
    },
    label: {
      type: String,
      default: 'Account',
    },
    placeholder: {
      type: String,
      default: 'Select an account',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialog: false,
      loading: false,
      fetchedOnce: false,
      accounts: [],
      accountById: {},
      searchDraft: '',
      searchApplied: '',
      pagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1,
      },
      columns: [
        { text: 'Account ID', value: 'accountId', sortable: false, width: 250 },
        { text: 'Account name', value: 'accountName', sortable: false },
        { text: '', value: 'action', sortable: false, align: 'end', width: 120 },
      ],
    };
  },
  computed: {
    selectedAccountId() {
      return this.value;
    },
    selectedLabel() {
      const id = this.selectedAccountId;
      if (!id) return '';
      return this.accountById[id]?.accountName || id;
    },
    displayAccounts() {
      const q = (this.searchApplied || '').trim().toLowerCase();
      if (!q) return this.accounts;
      return (this.accounts || []).filter(a => {
        const name = (a?.accountName || '').toLowerCase();
        const id = (a?.accountId || '').toLowerCase();
        return name.includes(q) || id.includes(q);
      });
    },
  },
  methods: {
    emitValue(next) {
      this.$emit('input', next);
      this.$emit('update:modelValue', next);
    },
    clearSelection() {
      this.emitValue(null);
    },
    openDialog() {
      if (this.disabled) return;
      this.dialog = true;

      if (!this.autoFetch) {
        // Lazy fetch: only call API when user opens selector
        if (!this.fetchedOnce) this.fetchAccounts({ page: 1, accountName: this.searchApplied });
        return;
      }

      // Auto fetch: ensure we have data (in case component mounted before dialog open)
      if (!this.fetchedOnce) this.fetchAccounts({ page: this.pagination.page, accountName: this.searchApplied });
    },
    async fetchAccounts({ page, accountName } = {}) {
      try {
        this.loading = true;
        const nextPage = page || this.pagination.page || 1;
        const q = (accountName != null ? accountName : this.searchApplied || '').trim();

        const { data } = await addFeeApi.getAvailableAccounts({
          page: nextPage,
          limit: this.pagination.limit,
          accountName: q || undefined,
        });

        this.accounts = Array.isArray(data?.data) ? data.data : [];

        const map = {};
        this.accounts.forEach(a => {
          if (a.accountId) map[a.accountId] = a;
        });
        this.accountById = { ...this.accountById, ...map };

        const pagination = data?.pagination;
        this.pagination.total = pagination.total || 0;
        this.pagination.page = pagination.page || nextPage;
        this.pagination.limit = pagination.limit || this.pagination.limit;
        this.pagination.totalPages = pagination.totalPages || 1;

        this.fetchedOnce = true;
      } catch (error) {
        console.error('Error fetching available accounts:', error);
        this.accounts = [];
        this.pagination.totalPages = 1;
      } finally {
        this.loading = false;
      }
    },
    onClearSearch() {
      this.pagination.page = 1;
      this.searchDraft = '';
      this.searchApplied = '';
      if (this.dialog) this.fetchAccounts({ page: 1, accountName: '' });
    },
    onSearchBlur() {
      if (!this.dialog) return;
      const next = (this.searchDraft || '').trim();
      if (next === (this.searchApplied || '')) return;
      this.searchApplied = next;
      this.pagination.page = 1;
      this.fetchAccounts({ page: 1, accountName: this.searchApplied });
    },
    selectAccount(item) {
      const id = item?.accountId || null;
      this.emitValue(id);
      this.dialog = false;
    },
  },
  mounted() {
    if (this.autoFetch) {
      this.fetchAccounts({ page: this.pagination.page });
    }
  },
  watch: {
    'pagination.page'(next) {
      if (!this.dialog) return;
      this.fetchAccounts({ page: next, accountName: this.searchApplied });
    },
  },
};
</script>

<style lang="scss" scoped>
button.btn.v-btn--outlined,
button.v-btn.v-btn--outlined {
  height: 28px !important;
}
.line-clamp-1 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  line-clamp: 1;
}
</style>
